// Services-related hooks

import { useMemo } from 'react';
import {
  useGetServicesQuery,
  useGetServiceQuery,
} from '@/services/api/services/servicesApi';
import type { MCPService, ServiceStatus } from '@/types/services';


interface UseServicesOptions {
  page?: number;
  limit?: number;
  search?: string;
  category?: string; // UUID
  sort?: 'recent' | 'rating' | 'name';
}

export const useServices = (options: UseServicesOptions = {}) => {
  const {
    data: servicesData,
    isLoading,
    error,
    refetch,
  } = useGetServicesQuery({
    page: options.page || 1,
    limit: options.limit || 20,
    search: options.search,
    category: options.category,
    sort: options.sort || 'recent',
  });

  const services = useMemo(() => servicesData?.data || [], [servicesData]);
  const pagination = useMemo(() => servicesData?.pagination, [servicesData]);
  const filters = useMemo(() => servicesData?.filters, [servicesData]);

  return {
    services,
    pagination,
    filters,
    isLoading,
    error,
    refetch,
  };
};

export const useService = (id: string) => {
  const {
    data: service,
    isLoading,
    error,
    refetch,
  } = useGetServiceQuery(id, { skip: !id });

  return {
    service,
    isLoading,
    error,
    refetch,
  };
};

export const useServiceStats = (services: MCPService[]) => {
  return useMemo(() => {
    const stats = services.reduce(
      (acc, service) => {
        acc.total += 1;
        acc.byStatus[service.status] = (acc.byStatus[service.status] || 0) + 1;
        acc.totalRevenue += service.metrics?.revenue || 0;
        acc.totalSubscriptions += service.metrics?.activeSubscriptions || 0;
        acc.totalRequests += service.metrics?.totalRequests || 0;
        return acc;
      },
      {
        total: 0,
        byStatus: {} as Record<ServiceStatus, number>,
        totalRevenue: 0,
        totalSubscriptions: 0,
        totalRequests: 0,
      }
    );

    return stats;
  }, [services]);
};
